/**
 * Pagina "Registri & movimenti".
 *
 * Due strumenti distinti:
 *   1. Ricerca registri RENTRI per unità locale (richiede num_iscr operatore + sito).
 *   2. **Validatore movimento live** (DM 59/2023, DD 143/2023): valida un singolo
 *      movimento contro le regole di business senza richiedere alcun registro
 *      RENTRI esistente. Pensato per la prova rapida e per chi sta preparando
 *      una bozza prima di inviarla.
 */
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useOperatoreContext } from "@/lib/operatoreContext";
import { getUserPreferences } from "@/components/PreferenzeOperative";
import { cn } from "@/lib/utils";
import { endpoints } from "@/api/client";
import type { ValidationIssue, DraftItem } from "@/api/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TBody, TD, TH, THead, TR } from "@/components/ui/table";
import { CenteredLoader, ErrorState, EmptyState } from "@/components/ui/feedback";
import {
  ChevronDown,
  Database,
  Search,
  ShieldCheck,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Sparkles,
  PlusCircle,
  Pencil,
  Send,
  Trash2,
  RefreshCw,
  Lock,
} from "lucide-react";
import { MovimentoDettaglioDialog } from "@/components/MovimentoDettaglioDialog";
import { MovimentoWizardDialog } from "@/components/MovimentoWizardDialog";
import { MovimentoStatoBadge } from "@/components/MovimentoStatoBadge";
import { InviaRegistroDialog } from "@/components/InviaRegistroDialog";
import { ModificaDescrizioneRegistroDialog } from "@/components/ModificaDescrizioneRegistroDialog";
import { NuovoRegistroDialog } from "@/components/NuovoRegistroDialog";
import { RegistroStatoBadge } from "@/components/RegistroStatoBadge";
import { MovimentoTipoBadge } from "@/components/MovimentoTipoBadge";
import { ChoiceSelect, ChoiceCombobox, ChoiceMultiSelect } from "@/components/forms";
import {
  UNITA_MISURA_FORM,
  normalizeCodificaItem,
  useRentriChoices,
  type RentriChoice,
} from "@/lib/rentriChoices";
import {
  movimentoRiferimenti,
  movimentoRowFields,
  type MovimentoRecord,
} from "@/lib/movimento";
import {
  resolveStatoUi,
  movimentoAnno,
  movimentoEerCode,
  movimentoProgressivo,
  movimentoTipoBadge,
  rentriModelStateMessage,
  rowDraftId,
  rowsHaveActivity,
} from "@/lib/movimentoUi";
import {
  registroAttivita,
  registroDataApertura,
  registroDescrizione,
  registroIdentificativo,
  registroProgressivoIniziale,
  registroDraftId,
  resolveRegistroStatoUi,
  type RegistroRow,
} from "@/lib/registro";

const PRESETS: { label: string; payload: Record<string, unknown> }[] = [
  {
    label: "Carico oli minerali (valido)",
    payload: {
      data: new Date().toISOString().slice(0, 10),
      causale: "C",
      eer: "130208",
      quantita: 250,
      unita_misura: "KG",
      caratteristiche_pericolo: ["HP14"],
    },
  },
  {
    label: "Quantità negativa (errore)",
    payload: {
      data: new Date().toISOString().slice(0, 10),
      causale: "C",
      eer: "170405",
      quantita: -5,
      unita_misura: "KG",
    },
  },
  {
    label: "EER pericoloso senza HP (warning)",
    payload: {
      data: new Date().toISOString().slice(0, 10),
      causale: "S",
      eer: "170410",
      quantita: 100,
      unita_misura: "KG",
    },
  },
  {
    label: "Causale errata + data futura (errori multipli)",
    payload: {
      data: "2099-12-31",
      causale: "PIPPO",
      eer: "ABCDEF",
      quantita: 0,
      unita_misura: "ton",
    },
  },
];

export function Registri() {
  const ctx = useOperatoreContext();
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeCf, setActiveCf] = useState<string | null>(() => ctx.cf || null);
  const [nuovoMovimentoOpen, setNuovoMovimentoOpen] = useState(false);
  const [movimentoDettaglio, setMovimentoDettaglio] = useState<MovimentoRecord | null>(null);
  const [inviaOpen, setInviaOpen] = useState(false);
  const [modificaDescrizioneOpen, setModificaDescrizioneOpen] = useState(false);
  const [filtroInviati, setFiltroInviati] = useState(
    () => getUserPreferences().registro_mostra_inviati === true,
  );
  const [filtroTipo, setFiltroTipo] = useState<"" | "C" | "S">("");
  const [filtroDal, setFiltroDal] = useState("");
  const [nuovoRegistroOpen, setNuovoRegistroOpen] = useState(false);
  const [registroEditDraft, setRegistroEditDraft] = useState<DraftItem | null>(
    null,
  );
  const [chiudendoId, setChiudendoId] = useState<string | null>(null);

  const registroId = ctx.registroId || null;
  const sito = ctx.numIscrSito;

  useEffect(() => {
    const r = searchParams.get("registro");
    const nuovo = searchParams.get("nuovo");
    if (r) ctx.setRegistroId(r);
    if (nuovo === "1" && r) setNuovoMovimentoOpen(true);
    if (r || nuovo) setSearchParams({}, { replace: true });
  }, []);

  useEffect(() => {
    if (ctx.cf && ctx.cf !== activeCf) setActiveCf(ctx.cf);
  }, [ctx.cf]);

  const operatoriQ = useQuery({
    queryKey: ["registri-operatori", activeCf],
    queryFn: () => endpoints.anagrafiche.operatori(activeCf!),
    enabled: !!activeCf,
    retry: false,
  });
  const operatore = (Array.isArray(operatoriQ.data) ? operatoriQ.data[0] : undefined) as
    | { num_iscr?: string; denominazione?: string }
    | undefined;
  const iscr = ctx.numIscr || operatore?.num_iscr || "";

  useEffect(() => {
    if (operatore?.num_iscr) {
      ctx.setOperatore(operatore.num_iscr, operatore.denominazione ?? "");
    }
  }, [operatore?.num_iscr, operatore?.denominazione]);

  // Lista siti
  const sitiQ = useQuery({
    queryKey: ["registri-siti", iscr],
    queryFn: () => endpoints.anagrafiche.siti(iscr),
    enabled: !!iscr,
    retry: false,
  });
  const siti = (Array.isArray(sitiQ.data) ? sitiQ.data : []) as Record<string, unknown>[];

  // Lista registri del sito selezionato (vista enriched: include bozze locali)
  const listQ = useQuery({
    queryKey: ["registri", iscr, sito],
    queryFn: () => endpoints.registri.lista(iscr, sito, "enriched"),
    enabled: !!iscr && !!sito,
    refetchInterval: (q) => {
      const data = q.state.data as { items?: RegistroRow[] } | unknown;
      const items = Array.isArray(data)
        ? (data as RegistroRow[])
        : ((data as { items?: RegistroRow[] } | undefined)?.items ?? []);
      // Polling 5s se ci sono righe in errore o in elaborazione (RENTRI demo
      // tipicamente sincrono, ma simmetria col flusso movimenti).
      const active = items.some((r) => {
        const s = resolveRegistroStatoUi(r);
        return s === "in_elaborazione" || s === "errore";
      });
      return active ? 5000 : false;
    },
  });

  const registriList: RegistroRow[] = Array.isArray(listQ.data)
    ? (listQ.data as RegistroRow[])
    : ((listQ.data as { items?: RegistroRow[] } | undefined)?.items ?? []);
  const selectedRegistroRow = registroId
    ? registriList.find((r) => registroIdentificativo(r) === registroId)
    : undefined;
  const selectedRegistroDescrizione = (() => {
    if (!selectedRegistroRow) return null;
    const d = registroDescrizione(selectedRegistroRow);
    return d !== "—" ? d : null;
  })();
  const selectedRegistroChiuso =
    selectedRegistroRow != null &&
    resolveRegistroStatoUi(selectedRegistroRow) === "chiuso";
  const registroIds = registriList
    .map((r) => registroIdentificativo(r))
    .filter((id) => id.length > 0);

  const movimentiCountsQ = useQueries({
    queries: registroIds.map((id) => ({
      queryKey: ["movimenti-count", id],
      queryFn: () => endpoints.registri.countMovimenti(id),
      enabled: !!id,
      staleTime: 30_000,
    })),
  });
  const movimentiCountById = new Map(
    registroIds.map((id, i) => [id, movimentiCountsQ[i]]),
  );

  const movQ = useQuery({
    queryKey: ["movimenti", registroId],
    queryFn: () => endpoints.registri.movimenti(registroId!, 1, "enriched"),
    enabled: !!registroId,
    refetchInterval: (q) => {
      const data = q.state.data as { items?: MovimentoRecord[] } | undefined;
      const rows = data?.items ?? [];
      return rowsHaveActivity(rows as MovimentoRecord[]) ? 5000 : false;
    },
  });

  const [draftEdit, setDraftEdit] = useState<DraftItem | null>(null);
  const [draftEditOpen, setDraftEditOpen] = useState(false);

  const apriEditBozza = async (mv: MovimentoRecord) => {
    const dId = rowDraftId(mv);
    if (!dId) return;
    try {
      const d = await endpoints.drafts.get(dId);
      setDraftEdit(d);
      setDraftEditOpen(true);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Bozza non recuperabile");
    }
  };

  const inviaMovimentoMut = useMutation({
    mutationFn: ({ draftId }: { draftId: string }) =>
      endpoints.registri.inviaMovimento(registroId!, draftId),
    onSuccess: (res) => {
      const status = res.rentri_status;
      if (status >= 400) {
        const msg =
          rentriModelStateMessage(res.rentri_body) ??
          (typeof res.rentri_body === "object" && res.rentri_body
            ? (res.rentri_body as Record<string, unknown>).detail ??
              (res.rentri_body as Record<string, unknown>).title
            : null) ??
          `RENTRI HTTP ${status}`;
        toast.error(`Invio rifiutato: ${String(msg)}`);
      } else {
        toast.success(
          status === 202 ? "Movimento in elaborazione" : "Movimento inviato",
        );
      }
      qc.invalidateQueries({ queryKey: ["movimenti", registroId] });
    },
    onError: (e: unknown) =>
      toast.error(e instanceof Error ? e.message : "Errore invio movimento"),
  });

  const eliminaBozzaMut = useMutation({
    mutationFn: ({ draftId }: { draftId: string }) =>
      endpoints.registri.eliminaBozzaMovimento(registroId!, draftId),
    onSuccess: () => {
      toast.success("Bozza eliminata");
      qc.invalidateQueries({ queryKey: ["movimenti", registroId] });
    },
    onError: (e: unknown) =>
      toast.error(e instanceof Error ? e.message : "Errore eliminazione"),
  });

  // ---- Mutazioni registro (guidato) ----
  const inviaRegistroBozzaMut = useMutation({
    mutationFn: ({ draftId }: { draftId: string }) =>
      endpoints.registri.inviaBozza(draftId),
    onSuccess: (res) => {
      const status = res.rentri_status;
      if (status >= 400) {
        const body = res.rentri_body;
        const detail =
          (typeof body === "object" && body
            ? ((body as Record<string, unknown>).detail ??
              (body as Record<string, unknown>).title)
            : null) ?? `RENTRI HTTP ${status}`;
        toast.error("Apertura registro rifiutata", {
          description: String(detail),
        });
      } else {
        toast.success(
          status === 202 ? "Registro in elaborazione" : "Registro aperto su RENTRI",
        );
      }
      qc.invalidateQueries({ queryKey: ["registri"] });
    },
    onError: (e: unknown) =>
      toast.error(e instanceof Error ? e.message : "Errore invio registro"),
  });

  const eliminaRegistroBozzaMut = useMutation({
    mutationFn: ({ draftId }: { draftId: string }) =>
      endpoints.registri.eliminaBozza(draftId),
    onSuccess: () => {
      toast.success("Bozza registro eliminata");
      qc.invalidateQueries({ queryKey: ["registri"] });
    },
    onError: (e: unknown) =>
      toast.error(
        e instanceof Error ? e.message : "Errore eliminazione bozza registro",
      ),
  });

  const chiudiRegistroMut = useMutation({
    mutationFn: ({ id }: { id: string }) => endpoints.registri.chiudi(id),
    onSuccess: (res, vars) => {
      const status = (res as { rentri_status?: number }).rentri_status ?? 0;
      if (status >= 200 && status < 300) {
        toast.success("Registro chiuso su RENTRI");
        if (registroId === vars.id) ctx.setRegistroId("");
      } else {
        const body = (res as { body?: unknown }).body;
        const detail =
          typeof body === "object" && body
            ? ((body as Record<string, unknown>).detail ??
              (body as Record<string, unknown>).title)
            : null;
        toast.error("RENTRI ha rifiutato la chiusura", {
          description: String(detail ?? `HTTP ${status}`),
        });
      }
      setChiudendoId(null);
      qc.invalidateQueries({ queryKey: ["registri"] });
    },
    onError: (e: unknown) => {
      setChiudendoId(null);
      toast.error(
        e instanceof Error ? e.message : "Errore chiusura registro",
      );
    },
  });

  const apriEditBozzaRegistro = async (registro: RegistroRow) => {
    const dId = registroDraftId(registro);
    if (!dId) return;
    try {
      const d = await endpoints.registri.getBozza(dId);
      setRegistroEditDraft(d);
    } catch (e) {
      toast.error(
        e instanceof Error ? e.message : "Bozza registro non recuperabile",
      );
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Registri & movimenti</h1>
          <p className="text-sm text-muted-foreground">
            Carico/scarico rifiuti — DM 59/2023, DD 143/2023
          </p>
        </div>
      </div>

      <MovimentoValidatorCollapsible />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Cerca registri di un'unità locale</CardTitle>
          <p className="text-xs text-muted-foreground">
            Sincronizzato con il contesto in sidebar (CF, UL, registro)
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="sm:col-span-2">
              <label className="text-xs font-medium text-muted-foreground">
                Codice fiscale o partita IVA operatore
              </label>
              <Input
                placeholder="CF o P.IVA operatore"
                value={ctx.cf}
                onChange={(e) => ctx.setCf(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    const v = ctx.cf.trim();
                    if (v) setActiveCf(v);
                  }
                }}
                className="font-mono"
              />
            </div>
            <Button
              onClick={() => {
                const v = ctx.cf.trim();
                if (v) setActiveCf(v);
              }}
              disabled={!ctx.cf.trim()}
              className="self-end"
            >
              <Search className="mr-2 h-4 w-4" /> Cerca
            </Button>
          </div>
          {operatoriQ.error && <ErrorState error={operatoriQ.error} />}
          {operatoriQ.isLoading && activeCf && <CenteredLoader />}
          {operatore && (
            <div className="flex flex-wrap items-center gap-2 rounded-md border bg-muted/30 p-2 text-xs">
              <Badge variant="secondary">{operatore.denominazione ?? "—"}</Badge>
              <span className="font-mono text-muted-foreground">
                num_iscr: {iscr || "—"}
              </span>
            </div>
          )}
          {iscr && (
            <div>
              <label className="text-xs font-medium text-muted-foreground">
                Unità locale
              </label>
              {sitiQ.isLoading ? (
                <CenteredLoader />
              ) : siti.length > 0 ? (
                <select
                  value={sito}
                  onChange={(e) => {
                    const opt = e.target.selectedOptions[0];
                    ctx.setSito(e.target.value, opt?.text?.split(" — ")[1] ?? opt?.text ?? "");
                  }}
                  className="mt-1 flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                >
                  <option value="">— Seleziona —</option>
                  {siti.map((s, i) => {
                    const id = String(s.num_iscr_sito ?? "");
                    const nome = String(s.nome ?? s.indirizzo ?? id);
                    return (
                      <option key={`${id}-${i}`} value={id}>
                        {id} — {nome}
                      </option>
                    );
                  })}
                </select>
              ) : (
                <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                  Nessuna unità locale trovata per questo operatore.
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Database className="h-5 w-5" /> Registri
              {registriList.length > 0 && (
                <Badge variant="outline" className="ml-1 align-middle font-normal">
                  {registriList.length}
                </Badge>
              )}
            </CardTitle>
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                onClick={() => setNuovoRegistroOpen(true)}
                disabled={!sito}
                title={
                  !sito
                    ? "Seleziona prima un'unità locale"
                    : "Apri il form di creazione registro"
                }
              >
                <PlusCircle className="mr-1.5 h-4 w-4" />
                Nuovo registro
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {listQ.isLoading && <CenteredLoader />}
          {listQ.error && <ErrorState error={listQ.error} />}
          {registriList.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
              <THead>
                <TR>
                  <TH>Identificativo</TH>
                  <TH>Descrizione</TH>
                  <TH>Attività</TH>
                  <TH>Progr. iniz.</TH>
                  <TH>Apertura</TH>
                  <TH>Stato</TH>
                  <TH className="text-right">N. movimenti</TH>
                  <TH />
                </TR>
              </THead>
              <TBody>
                {registriList.map((r, i) => {
                  const id = registroIdentificativo(r);
                  const draftId = registroDraftId(r);
                  const stato = resolveRegistroStatoUi(r);
                  const countQ =
                    id && stato !== "bozza" && stato !== "errore"
                      ? movimentiCountById.get(id)
                      : undefined;
                  const count = typeof countQ?.data === "number" ? countQ.data : null;
                  const descrizione = registroDescrizione(r);
                  const selected = !!id && id === registroId;
                  const isDraftRow = stato === "bozza" || stato === "errore";
                  return (
                  <TR
                    key={id || draftId || i}
                    data-state={selected ? "selected" : undefined}
                    className={
                      selected
                        ? "bg-primary/5 [&>td:first-child]:border-l-2 [&>td:first-child]:border-l-primary"
                        : undefined
                    }
                  >
                    <TD className="font-mono text-xs whitespace-nowrap">
                      {id || (
                        <span className="text-muted-foreground italic">bozza</span>
                      )}
                    </TD>
                    <TD
                      className="max-w-[220px] truncate text-sm"
                      title={descrizione !== "—" ? descrizione : undefined}
                    >
                      {descrizione}
                    </TD>
                    <TD className="text-sm">{registroAttivita(r)}</TD>
                    <TD className="text-xs whitespace-nowrap text-muted-foreground">
                      {registroProgressivoIniziale(r)}
                    </TD>
                    <TD className="text-xs whitespace-nowrap">
                      {registroDataApertura(r)}
                    </TD>
                    <TD>
                      <RegistroStatoBadge registro={r} />
                    </TD>
                    <TD className="text-right tabular-nums">
                      {isDraftRow ? (
                        <span className="text-muted-foreground">—</span>
                      ) : countQ?.isLoading ? (
                        <span className="text-muted-foreground">…</span>
                      ) : countQ?.isError ? (
                        <span
                          className="text-muted-foreground"
                          title="Conteggio non disponibile"
                        >
                          —
                        </span>
                      ) : (
                        <Badge variant={count === 0 ? "outline" : "default"}>
                          {count ?? 0}
                        </Badge>
                      )}
                    </TD>
                    <TD className="text-right whitespace-nowrap">
                      <div className="flex items-center justify-end gap-1">
                        {isDraftRow && draftId && (
                          <>
                            <Button
                              size="sm"
                              variant="ghost"
                              title={
                                stato === "errore"
                                  ? "Modifica e correggi i dati"
                                  : "Modifica bozza"
                              }
                              onClick={() => apriEditBozzaRegistro(r)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              title={
                                stato === "errore"
                                  ? "Rinvia a RENTRI"
                                  : "Apri su RENTRI"
                              }
                              disabled={inviaRegistroBozzaMut.isPending}
                              onClick={() =>
                                inviaRegistroBozzaMut.mutate({ draftId })
                              }
                            >
                              {stato === "errore" ? (
                                <RefreshCw className="h-4 w-4" />
                              ) : (
                                <Send className="h-4 w-4" />
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              title="Elimina bozza"
                              disabled={eliminaRegistroBozzaMut.isPending}
                              onClick={() => {
                                if (
                                  window.confirm(
                                    "Eliminare definitivamente questa bozza di registro?",
                                  )
                                ) {
                                  eliminaRegistroBozzaMut.mutate({ draftId });
                                }
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                        {stato === "attivo" && id && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => ctx.setRegistroId(id)}
                            >
                              Movimenti
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              title="Chiudi registro su RENTRI"
                              disabled={
                                chiudiRegistroMut.isPending && chiudendoId === id
                              }
                              onClick={() => {
                                if (
                                  window.confirm(
                                    `Chiudere il registro ${id}?\nSu RENTRI il registro non potrà più ricevere movimenti.`,
                                  )
                                ) {
                                  setChiudendoId(id);
                                  chiudiRegistroMut.mutate({ id });
                                }
                              }}
                            >
                              <Lock className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                        {stato === "chiuso" && id && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => ctx.setRegistroId(id)}
                          >
                            Movimenti
                          </Button>
                        )}
                      </div>
                    </TD>
                  </TR>
                  );
                })}
              </TBody>
              </Table>
            </div>
          ) : (
            !listQ.isLoading &&
            !listQ.error && (
              <EmptyState
                title="Cerca i registri di un'unità locale"
                description="Inserisci numero iscrizione operatore e sito"
              />
            )
          )}
        </CardContent>
      </Card>

      {registroId && (
        <Card>
          <CardHeader>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <CardTitle className="text-base flex flex-wrap items-center gap-x-2 gap-y-1">
                <span>
                  Movimenti del registro{" "}
                  <span className="font-mono text-xs">{registroId}</span>
                  {selectedRegistroDescrizione && (
                    <>
                      <span className="text-muted-foreground"> — </span>
                      <span className="text-sm font-normal">{selectedRegistroDescrizione}</span>
                    </>
                  )}
                </span>
                {(() => {
                  const cq = movimentiCountById.get(registroId);
                  const n = typeof cq?.data === "number" ? cq.data : null;
                  if (n === null) return null;
                  return (
                    <Badge variant="outline" className="align-middle font-normal">
                      {n} {n === 1 ? "movimento" : "movimenti"}
                    </Badge>
                  );
                })()}
              </CardTitle>
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setModificaDescrizioneOpen(true)}
                  disabled={selectedRegistroChiuso}
                  title={
                    selectedRegistroChiuso
                      ? "Registro chiuso: la descrizione non è modificabile"
                      : "Aggiorna la descrizione del registro su RENTRI"
                  }
                >
                  <Pencil className="mr-1.5 h-4 w-4" />
                  Modifica descrizione
                </Button>
                <Button size="sm" onClick={() => setInviaOpen(true)}>
                  <Send className="mr-1.5 h-4 w-4" />
                  Trasmissione e export
                </Button>
                <NuovoMovimentoButton
                  registroId={registroId}
                  initialOpen={nuovoMovimentoOpen}
                  onOpenChange={setNuovoMovimentoOpen}
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="mb-4 flex flex-wrap items-center gap-3 text-sm">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={filtroInviati}
                  onChange={(e) => setFiltroInviati(e.target.checked)}
                />
                Inviati
              </label>
              <select
                className="h-8 rounded-md border border-input bg-background px-2 text-xs"
                value={filtroTipo}
                onChange={(e) => setFiltroTipo(e.target.value as "" | "C" | "S")}
              >
                <option value="">Tipo</option>
                <option value="C">Carico</option>
                <option value="S">Scarico</option>
              </select>
              <label className="flex items-center gap-2 text-xs text-muted-foreground">
                Dal
                <Input
                  type="date"
                  className="h-8 w-36"
                  value={filtroDal}
                  onChange={(e) => setFiltroDal(e.target.value)}
                />
              </label>
            </div>
            {movQ.isLoading && <CenteredLoader />}
            {movQ.error && <ErrorState error={movQ.error} />}
            {(() => {
              const raw = Array.isArray(movQ.data)
                ? (movQ.data as unknown[])
                : ((movQ.data as { items?: unknown[] } | undefined)?.items ?? []);
              const movimenti = (raw as MovimentoRecord[]).filter((m) => {
                const badge = movimentoTipoBadge(m);
                if (filtroTipo === "C" && badge.letter !== "C") return false;
                if (filtroTipo === "S" && badge.letter !== "S") return false;
                const stato = resolveStatoUi(m);
                if (filtroInviati && stato === "bozza") return false;
                if (filtroDal) {
                  const raw = movimentoRiferimenti(m).data_ora_registrazione ?? m.data;
                  const d = String(raw ?? "").slice(0, 10);
                  if (d && d < filtroDal) return false;
                }
                return true;
              });
              if (movimenti.length === 0) {
                const totalRaw = (raw as MovimentoRecord[]).length;
                const filtriAttivi = filtroInviati || !!filtroTipo || !!filtroDal;
                const nascostiDaFiltri = totalRaw > 0 && filtriAttivi;
                return (
                  <EmptyState
                    title="Nessun movimento"
                    description={
                      nascostiDaFiltri
                        ? "Con i filtri attuali non risulta alcun movimento (ad es. le bozze sono nascoste se è selezionato solo «Inviati»). Modifica i filtri sopra oppure creane uno con «Nuovo movimento»."
                        : 'Clicca "+ Nuovo movimento": una sola azione per validare, salvare e inviare a RENTRI.'
                    }
                  />
                );
              }
              return (
                <>
                <div className="overflow-x-auto">
                <Table>
                  <THead>
                    <TR>
                      <TH>Tipo</TH>
                      <TH>No.</TH>
                      <TH>Data</TH>
                      <TH>CER</TH>
                      <TH>Compilazione</TH>
                      <TH>Stato</TH>
                      <TH className="text-right">Quantità</TH>
                      <TH className="text-right">Azioni</TH>
                    </TR>
                  </THead>
                  <TBody>
                    {movimenti.map((m, i) => {
                      const row = movimentoRowFields(m);
                      const stato = resolveStatoUi(m);
                      const prog = movimentoProgressivo(m);
                      const anno = movimentoAnno(m);
                      const draftId = rowDraftId(m);
                      const isEditable =
                        stato === "bozza" || stato === "errore";
                      const isVirtual = !m.identificativo_movimento && !prog;
                      return (
                        <TR
                          key={
                            row.identificativo !== "—"
                              ? row.identificativo
                              : draftId ?? i
                          }
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={() => {
                            if (isVirtual && isEditable) {
                              apriEditBozza(m);
                            } else {
                              setMovimentoDettaglio(m);
                            }
                          }}
                        >
                          <TD>
                            <MovimentoTipoBadge movimento={m} />
                          </TD>
                          <TD className="font-mono text-xs tabular-nums">
                            {prog != null && anno != null
                              ? `${prog}/${String(anno).slice(-2)}`
                              : row.numero}
                          </TD>
                          <TD className="text-xs whitespace-nowrap">{row.data}</TD>
                          <TD className="font-mono text-xs">{movimentoEerCode(m)}</TD>
                          <TD className="font-mono text-[10px] text-muted-foreground max-w-[120px] truncate">
                            {row.identificativo}
                          </TD>
                          <TD>
                            <MovimentoStatoBadge movimento={m} />
                          </TD>
                          <TD className="text-right text-sm tabular-nums">
                            {row.quantita} {row.um}
                          </TD>
                          <TD
                            className="text-right whitespace-nowrap"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="flex items-center justify-end gap-1">
                              {isEditable && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  title="Modifica"
                                  onClick={() => apriEditBozza(m)}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                              )}
                              {(stato === "bozza" || stato === "errore") && draftId && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  title={stato === "errore" ? "Rinvia" : "Invia a RENTRI"}
                                  disabled={inviaMovimentoMut.isPending}
                                  onClick={() =>
                                    inviaMovimentoMut.mutate({ draftId })
                                  }
                                >
                                  {stato === "errore" ? (
                                    <RefreshCw className="h-4 w-4" />
                                  ) : (
                                    <Send className="h-4 w-4" />
                                  )}
                                </Button>
                              )}
                              {isEditable && draftId && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  title="Elimina bozza"
                                  disabled={eliminaBozzaMut.isPending}
                                  onClick={() => {
                                    if (
                                      window.confirm(
                                        "Eliminare definitivamente questa bozza?",
                                      )
                                    ) {
                                      eliminaBozzaMut.mutate({ draftId });
                                    }
                                  }}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                              {!isVirtual && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  title="Dettaglio"
                                  onClick={() => setMovimentoDettaglio(m)}
                                >
                                  Dettaglio
                                </Button>
                              )}
                            </div>
                          </TD>
                        </TR>
                      );
                    })}
                  </TBody>
                </Table>
                </div>
                </>
              );
            })()}
          </CardContent>
          <MovimentoDettaglioDialog
            movimento={movimentoDettaglio}
            open={!!movimentoDettaglio}
            onOpenChange={(o) => !o && setMovimentoDettaglio(null)}
          />
          <InviaRegistroDialog
            registroId={registroId}
            open={inviaOpen}
            onOpenChange={setInviaOpen}
          />
          <ModificaDescrizioneRegistroDialog
            registroId={registroId}
            descrizioneAttuale={
              selectedRegistroRow ? registroDescrizione(selectedRegistroRow) : "—"
            }
            registriListQueryKey={["registri", iscr, sito]}
            chiuso={selectedRegistroChiuso}
            open={modificaDescrizioneOpen}
            onOpenChange={setModificaDescrizioneOpen}
          />
          {draftEditOpen && draftEdit && (
            <MovimentoWizardDialog
              registroId={registroId}
              draft={draftEdit}
              onClose={() => {
                setDraftEditOpen(false);
                setDraftEdit(null);
              }}
            />
          )}
        </Card>
      )}

      <NuovoRegistroDialog
        open={nuovoRegistroOpen}
        onOpenChange={setNuovoRegistroOpen}
        numIscrSito={sito}
        numIscrSitoLabel={(() => {
          const s = siti.find(
            (x) => String(x.num_iscr_sito ?? "") === sito,
          );
          return s ? String(s.nome ?? s.indirizzo ?? "") : undefined;
        })()}
      />
      {registroEditDraft && (
        <NuovoRegistroDialog
          open={!!registroEditDraft}
          onOpenChange={(o) => {
            if (!o) setRegistroEditDraft(null);
          }}
          numIscrSito={
            (registroEditDraft.payload as Record<string, unknown> | null)?.[
              "num_iscr_sito"
            ] as string | undefined ?? sito
          }
          draft={registroEditDraft}
          onSaved={() => setRegistroEditDraft(null)}
          onSent={() => setRegistroEditDraft(null)}
        />
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Pulsante "+ Nuovo movimento" → dialog con form + validazione live + salva bozza
// ---------------------------------------------------------------------------

function NuovoMovimentoButton({
  registroId,
  initialOpen = false,
  onOpenChange,
}: {
  registroId: string;
  initialOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const [open, setOpen] = useState(initialOpen);

  useEffect(() => {
    if (initialOpen) setOpen(true);
  }, [initialOpen]);

  const setOpenState = (v: boolean) => {
    setOpen(v);
    onOpenChange?.(v);
  };

  return (
    <>
      <Button size="sm" onClick={() => setOpenState(true)}>
        <PlusCircle className="mr-1.5 h-4 w-4" /> Nuovo movimento
      </Button>
      {open && (
        <MovimentoWizardDialog
          registroId={registroId}
          onClose={() => setOpenState(false)}
        />
      )}
    </>
  );
}


// ---------------------------------------------------------------------------
// Validatore movimento live (collassabile)
// ---------------------------------------------------------------------------

function MovimentoValidatorCollapsible() {
  const [open, setOpen] = useState(false);
  return (
    <Card>
      <button
        type="button"
        className="flex w-full items-center justify-between px-6 py-4 text-left"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="text-base font-medium flex items-center gap-2">
          <ShieldCheck className="h-5 w-5" /> Validatore movimento (anteprima regole)
        </span>
        <ChevronDown className={cn("h-5 w-5 transition", open && "rotate-180")} />
      </button>
      {open && (
        <CardContent className="border-t pt-0">
          <MovimentoValidator />
        </CardContent>
      )}
    </Card>
  );
}

function MovimentoValidator() {
  const [data, setData] = useState(() => new Date().toISOString().slice(0, 10));
  const [causale, setCausale] = useState("C");
  const [eer, setEer] = useState("170405");
  const [qta, setQta] = useState<string>("100");
  const [um, setUm] = useState("KG");
  const [hp, setHp] = useState<string[]>([]);

  const hpQ = useRentriChoices("caratteristiche-pericolo");
  const hpOptions = hpQ.data ?? [];

  // Causali del validatore locale (non sono le DM 59 del registro)
  const causaliValidator: RentriChoice[] = [
    { value: "C", label: "C", description: "Carico" },
    { value: "S", label: "S", description: "Scarico" },
    { value: "CC", label: "CC", description: "Carico — comunicazione cumulativa" },
    { value: "CS", label: "CS", description: "Scarico — comunicazione cumulativa" },
    { value: "GP", label: "GP", description: "Giacenza periodica" },
    { value: "RP", label: "RP", description: "Rettifica periodica" },
  ];

  const eerQ = useQuery({
    queryKey: ["codifiche-eer-choices"],
    queryFn: async (): Promise<RentriChoice[]> => {
      const rows = await endpoints.codifiche.lookup("codici-eer");
      const list = Array.isArray(rows) ? rows : [];
      return list
        .map((r) => {
          const c = normalizeCodificaItem(r);
          if (!c) return null;
          return { ...c, value: c.value.replace(/\./g, "") };
        })
        .filter((x): x is RentriChoice => !!x);
    },
    staleTime: 24 * 60 * 60 * 1000,
  });
  const eerOptions = eerQ.data ?? [];

  const mut = useMutation({
    mutationFn: (payload: Record<string, unknown>) => endpoints.validate.movimento(payload),
  });

  const submit = () => {
    const payload: Record<string, unknown> = {
      data,
      causale: causale.toUpperCase(),
      eer,
      quantita: qta === "" ? null : Number(qta),
      unita_misura: um,
    };
    if (hp.length > 0) {
      payload.caratteristiche_pericolo = hp;
    }
    mut.mutate(payload);
  };

  const applyPreset = (p: (typeof PRESETS)[number]) => {
    const x = p.payload as Record<string, unknown>;
    setData(String(x.data ?? new Date().toISOString().slice(0, 10)));
    setCausale(String(x.causale ?? "C"));
    setEer(String(x.eer ?? ""));
    setQta(String(x.quantita ?? ""));
    setUm(String(x.unita_misura ?? "KG"));
    setHp(Array.isArray(x.caratteristiche_pericolo)
      ? (x.caratteristiche_pericolo as string[])
      : []);
    // Auto-submit subito dopo aver settato i valori
    setTimeout(() => mut.mutate(x), 50);
  };

  const result = mut.data;

  return (
    <div className="space-y-4 pt-2">
        <p className="text-xs text-muted-foreground">
          Regole DM 59/2023 — applica subito senza inviare a RENTRI
        </p>
        <div className="flex flex-wrap gap-2">
          {PRESETS.map((p) => (
            <Button
              key={p.label}
              type="button"
              size="sm"
              variant="outline"
              onClick={() => applyPreset(p)}
            >
              <Sparkles className="mr-1.5 h-3.5 w-3.5" />
              {p.label}
            </Button>
          ))}
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Field label="Data movimento">
            <Input type="date" value={data} onChange={(e) => setData(e.target.value)} />
          </Field>
          <Field label="Causale">
            <ChoiceSelect
              value={causale}
              onChange={(v) => setCausale(v || "C")}
              options={causaliValidator}
              required
            />
          </Field>
          <Field label="Codice EER">
            <ChoiceCombobox
              value={eer}
              onChange={(v) => setEer(v.replace(/\./g, ""))}
              options={eerOptions}
              loading={eerQ.isLoading}
              formatValue={(v) => {
                const clean = v.replace(/\./g, "");
                return clean.length === 6
                  ? `${clean.slice(0, 2)}.${clean.slice(2, 4)}.${clean.slice(4)}`
                  : clean;
              }}
            />
          </Field>
          <Field label="Quantità">
            <Input
              type="number"
              value={qta}
              onChange={(e) => setQta(e.target.value)}
              placeholder="100"
            />
          </Field>
          <Field label="Unità di misura">
            <ChoiceSelect
              value={um}
              onChange={(v) => setUm(v || "KG")}
              options={UNITA_MISURA_FORM}
              required
            />
          </Field>
          <Field label="Caratteristiche di pericolo (HP)">
            <ChoiceMultiSelect
              values={hp}
              onChange={setHp}
              options={hpOptions}
              loading={hpQ.isLoading}
              emptyText="Nessuna caratteristica HP disponibile"
            />
          </Field>
        </div>

        <div className="flex items-center gap-2">
          <Button onClick={submit} disabled={mut.isPending}>
            {mut.isPending ? "Validazione…" : "Valida movimento"}
          </Button>
          {mut.error && <ErrorState error={mut.error} />}
        </div>

        {result && <ValidationOutcome valid={result.valid} issues={result.issues} />}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="text-xs font-medium text-muted-foreground">{label}</label>
      {children}
    </div>
  );
}

const VALIDATION_SUBJECT_LABELS = {
  movimento: { valid: "Movimento valido", invalid: "Movimento non valido" },
  registro: { valid: "Registro valido", invalid: "Registro non valido" },
} as const;

function ValidationOutcome({
  valid,
  issues,
  subject = "movimento",
}: {
  valid: boolean;
  issues: ValidationIssue[];
  subject?: keyof typeof VALIDATION_SUBJECT_LABELS;
}) {
  const errors = issues.filter((i) => i.severity === "error");
  const warnings = issues.filter((i) => i.severity === "warning");
  const labels = VALIDATION_SUBJECT_LABELS[subject];

  return (
    <div className="space-y-3">
      <div
        className={`flex items-center gap-3 rounded-md border p-3 ${
          valid
            ? "border-emerald-500/30 bg-emerald-500/5 text-emerald-700 dark:text-emerald-400"
            : "border-rose-500/30 bg-rose-500/5 text-rose-700 dark:text-rose-400"
        }`}
      >
        {valid ? <CheckCircle2 className="h-5 w-5" /> : <XCircle className="h-5 w-5" />}
        <div className="text-sm">
          <span className="font-semibold">
            {valid ? labels.valid : labels.invalid}
          </span>
          <span className="ml-2 text-xs opacity-80">
            {errors.length} errori · {warnings.length} avvisi
          </span>
        </div>
      </div>

      {issues.length > 0 && (
        <div className="space-y-2">
          {issues.map((it, idx) => (
            <div
              key={idx}
              className={`flex items-start gap-3 rounded-md border p-3 text-sm ${
                it.severity === "error"
                  ? "border-rose-500/30 bg-rose-500/5"
                  : "border-amber-500/30 bg-amber-500/5"
              }`}
            >
              {it.severity === "error" ? (
                <XCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-rose-500" />
              ) : (
                <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-500" />
              )}
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant={it.severity === "error" ? "destructive" : "secondary"}>
                    {it.code}
                  </Badge>
                  <span className="font-mono text-xs text-muted-foreground">
                    {it.field}
                  </span>
                </div>
                <p className="mt-1 text-foreground/90">{it.message}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
